import React, { useEffect, useState } from 'react'
import Sidebar from './Sidebar'
import { Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Navigate } from 'react-router-dom'
import { settingsService, invoiceService, purchaseOrderService } from '../services/api'
import { MOBILE_BLOCK_SETTING_EVENT } from '../utils/mobileViewPolicy'

const SIDEBAR_STORAGE_KEY = 'oms-sidebar-open'

export default function Layout() {
  const { user } = useAuth()
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === 'undefined') return true

    const storedState = window.localStorage.getItem(SIDEBAR_STORAGE_KEY)
    if (storedState === null) return window.innerWidth >= 1024

    return storedState === 'true'
  })
  const [isMobileView, setIsMobileView] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 768 : false))
  const [invoiceReminderCount, setInvoiceReminderCount] = useState(0)
  const [poReminderCount, setPoReminderCount] = useState(0)
  const location = useLocation()

  const toggleSidebar = () => {
    setIsSidebarOpen((prevState) => !prevState)
  }

  useEffect(() => {
    if (typeof window === 'undefined') return

    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarOpen))
  }, [isSidebarOpen])

  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchInvoiceReminderCount = async () => {
    try {
      const response = await invoiceService.getAll()
      const invoices = response.data.data || []
      const now = Date.now()
      const thirtyDaysMs = 30 * 24 * 60 * 60 * 1000
      const count = invoices.filter((inv) => {
        const status = String(inv.status || '').trim().toLowerCase()
        if (status === 'paid') return false
        const issuedDate = new Date(inv.invoice_date).getTime()
        return (now - issuedDate) > thirtyDaysMs
      }).length
      setInvoiceReminderCount(count)
    } catch {
      // Silently ignore — the badge simply won't show
    }
  }

  const fetchPoReminderCount = async () => {
    try {
      const response = await purchaseOrderService.getAll()
      const purchaseOrders = response.data.data || []

      // Calculate profit margin for each PO
      // Margin = (PO Value - Total Costs) / PO Value * 100
      const MARGIN_THRESHOLD = 20 // Show reminder if margin < 20%

      const count = purchaseOrders.filter((po) => {
        const poValue = Number(po.po_value || 0)
        if (poValue <= 0) return false

        // Calculate costs (you may need to adjust this based on your cost structure)
        // For now, assuming costs need to be calculated from related expenses/invoices
        // This is a simplified version - you may need to fetch actual cost data
        const estimatedCosts = poValue * 0.5 // Assuming 50% costs as placeholder
        const margin = ((poValue - estimatedCosts) / poValue) * 100

        return margin < MARGIN_THRESHOLD
      }).length

      setPoReminderCount(count)
    } catch (error) {
      console.error('Error fetching PO reminders:', error)
      // Silently ignore — the badge simply won't show
    }
  }

  useEffect(() => {
    fetchInvoiceReminderCount()
    fetchPoReminderCount()
  }, [user?.user_id, location.pathname])

  useEffect(() => {
    if (!isMobileView) return
    setIsSidebarOpen(false)
  }, [location.pathname, isMobileView])

  useEffect(() => {
    const syncMobilePolicyFlag = async () => {
      try {
        await settingsService.getMobileBlockView()
      } catch {
        // Ignore this policy fetch in layout. All pages should remain reachable on mobile.
      }
    }

    const handlePolicyUpdate = () => {
      syncMobilePolicyFlag()
    }

    syncMobilePolicyFlag()
    window.addEventListener(MOBILE_BLOCK_SETTING_EVENT, handlePolicyUpdate)
    return () => window.removeEventListener(MOBILE_BLOCK_SETTING_EVENT, handlePolicyUpdate)
  }, [user?.user_id])

  if (!user) {
    return <Navigate to="/login" />
  }

  return (
    <div className="flex h-dvh bg-gray-100 overflow-hidden">
      <Sidebar
        isOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
        isMobile={isMobileView}
        onNavigate={() => setIsSidebarOpen(false)}
        reminderCount={invoiceReminderCount}
        poReminderCount={poReminderCount}
      />

      {isMobileView && isSidebarOpen && (
        <button
          type="button"
          aria-label="Close menu overlay"
          onClick={() => setIsSidebarOpen(false)}
          className="fixed inset-0 z-30 bg-slate-900/40"
        />
      )}

      <main className="min-w-0 flex-1 overflow-y-auto">
        {isMobileView && (
          <div className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white/95 px-3 py-2 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
            <button
              type="button"
              onClick={toggleSidebar}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-semibold text-slate-700 dark:border-slate-600 dark:text-slate-100"
            >
              Menu
            </button>
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">OMS</span>
          </div>
        )}
        <div className="p-3 sm:p-4 lg:p-6">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
